import {
    SORT_PATH_FILL_ALGORITHMS,
    SORT_PATH_PICK_ALGORITHMS,
    SORT_PATH_SETTING_STATUS,
    SortingOrder,
    StationCycleState,
} from "../Constants";
import {
    SORT_PATH_CSV_HEADERS,
    SORT_PATH_CLUSTER_PARAMS_HEADERS,
    VALID_SPLIT_PERCENTAGES,
    VALID_COLUMN_LENGTHS,
    VALID_ADTA_ORDERS,
    MAX_NAME_LENGTH,
    MAX_SIDELINE_SORT_ZONES,
    MAX_ROWS,
    CSV_DELIMITER,
    KEY_SEPARATOR,
    ARRAY_SEPARATOR,
    RANGE_SEPARATOR,
    NEWLINE_DELIMITER,
    VALID_FILL_ALGORITHMS,
    VALID_PICK_ALGORITHMS,
    VALID_ORDER_TYPES,
    VALID_AISLE_ORDER_TYPES,
    VALID_PICK_AISLE_ORDER_TYPES,
    DEFAULT_COLUMN_SPLIT_RATIO,
    NAME_SEPARATOR,
    LIST_SEPARATOR,
    INCLUDED_AISLE_START,
    INCLUDED_AISLE_END,
    INCLUDED_LOCATION_START,
    INCLUDED_LOCATION_END,
    TRUE_STRING,
    FALSE_STRING,
    EFFECTIVE_DATE_PATTERN,
} from "../constants/SortPathBulkUploadConstants";
import {toDateInputValue} from "./PathScheduleHelper";
import {getStationCycles, MAX_CONFIGS_PER_CYCLE} from "./SortPathCycleHelper";
import {areSameCycles} from "../components/util/sort-paths/CommonUtils";

/** The row's setting name (user-provided, else generated), shared by validation and create/update lookup. */
export function resolveRowName(row) {
    return row[SORT_PATH_CSV_HEADERS.NAME]
        ? row[SORT_PATH_CSV_HEADERS.NAME]
        : generateSettingName(row[SORT_PATH_CSV_HEADERS.CYCLE], row[SORT_PATH_CSV_HEADERS.FILL_ALGORITHM]);
}

/** Case-insensitive, whitespace-trimmed name comparison key. */
function normalizeName(name) {
    return (name || "").trim().toLowerCase();
}

/** Counts existing settings targeting station + cycle via scalar nodeId/cycle or cycleList (as the form cap). */
export function countConfigsForCycle(existingConfigs, nodeId, cycle) {
    if (!Array.isArray(existingConfigs)) return 0;
    const [target] = getStationCycles({nodeId, cycle});
    // A blank cycle has no station/cycle pair to match (validateRow reports the missing field).
    if (!target) return 0;
    return existingConfigs.filter(setting =>
        getStationCycles(setting).some(existing => areSameCycles(existing, target))).length;
}

/** True when an existing config at the station (owner nodeId or any cycleList entry) has this name. */
function isNameTakenAtStation(existingConfigs, nodeId, name) {
    return Array.isArray(existingConfigs) && existingConfigs.some(config => normalizeName(config.name) === normalizeName(name)
        && (config.nodeId === nodeId || getStationCycles(config).some(sc => sc[StationCycleState.STATION_CODE] === nodeId)));
}

/**
 * Builds a row object from header-value pairs.
 * Returns { row } on success, or { error } if column count mismatches.
 */
function buildRowFromLine(headers, line, rowIndex, fileLabel) {
    const values = line.split(CSV_DELIMITER).map(function(value) { return value.trim(); });
    if (values.length !== headers.length) {
        return { error: fileLabel + " row " + rowIndex + " has " + values.length + " columns but header has " + headers.length + ". Check for missing or extra commas." };
    }
    const row = {};
    headers.forEach(function(header, index) {
        row[header] = values[index] || "";
    });
    return { row };
}

/**
 * Splits a semicolon-separated string into a trimmed array.
 */
function toArray(str) {
    if (!str) return [];
    return str.split(ARRAY_SEPARATOR).map(function(item) { return item.trim(); }).filter(function(item) { return item.length > 0; });
}

/**
 * Parses semicolon-separated range strings into [{startKey, endKey}] objects.
 * Used for both aisle ranges and location ranges.
 */
function parseRanges(str, startKey, endKey) {
    if (!str) return [];
    return toArray(str).map(function(range) {
        const parts = range.split(RANGE_SEPARATOR);
        const result = {};
        result[startKey] = parts[0].trim();
        result[endKey] = parts[1] ? parts[1].trim() : parts[0].trim();
        return result;
    });
}

/**
 * Checks if any value in a semicolon-separated string contains a range separator (:).
 * Used to reject ranges in fields that only accept individual labels.
 */
function containsRangeSeparator(str) {
    if (!str) return false;
    return toArray(str).some(function(item) { return item.includes(RANGE_SEPARATOR); });
}

/**
 * Builds the common fill fields shared across STANDARD_BLOCK, COMBINATRIX, and CLUSTER_WISE_STANDARD.
 * - balancingSortZonesInClusters defaults to false (disabled unless explicitly "true")
 * - optimiseClusterUsage defaults to true (enabled unless explicitly "false")
 */
function buildCommonFillFields(row) {
    return {
        adtaPrioritizationOrder: row[SORT_PATH_CSV_HEADERS.FILL_ADTA_PRIORITIZATION_ORDER] || null,
        deprioritizedTiers: toArray(row[SORT_PATH_CSV_HEADERS.FILL_DEPRIORITIZED_TIERS]),
        deprioritizedTiersForADTAClusters: toArray(row[SORT_PATH_CSV_HEADERS.FILL_DEPRIORITIZED_TIERS_ADTA]),
        scienceModelOptimizationParameter: {
            balancingSortZonesInClusters: (row[SORT_PATH_CSV_HEADERS.BALANCING_SORT_ZONES] || "").toLowerCase() === TRUE_STRING,
            optimiseClusterUsage: (row[SORT_PATH_CSV_HEADERS.OPTIMISE_CLUSTER_USAGE] || "").toLowerCase() !== FALSE_STRING,
        },
    };
}

/**
 * Splits CSV text into non-empty trimmed lines and validates headers.
 * Returns { lines, headers } on success or { error } on failure.
 */
function parseCSVLines(csvText, requiredHeaders, errorPrefix) {
    const lines = csvText.split(NEWLINE_DELIMITER).filter(function(line) { return line.trim().length > 0; });
    if (lines.length < 2) {
        return { error: errorPrefix + " must have a header row and at least one data row" };
    }
    const headers = lines[0].split(CSV_DELIMITER).map(function(header) { return header.trim(); });
    const missingHeaders = requiredHeaders.filter(function(requiredHeader) { return !headers.includes(requiredHeader); });
    if (missingHeaders.length > 0) {
        return { error: errorPrefix + " missing required headers: " + missingHeaders.join(LIST_SEPARATOR) };
    }
    return { lines: lines, headers: headers };
}

/**
 * Generates the auto-generated setting name from cycle and algorithm.
 * Falls back to cycle-only if combined name exceeds MAX_NAME_LENGTH.
 */
export function generateSettingName(cycle, fillAlgorithm) {
    let name = cycle + NAME_SEPARATOR + fillAlgorithm;
    if (name.length > MAX_NAME_LENGTH) {
        name = cycle;
    }
    return name;
}

/**
 * Parses a CSV string into an array of row objects keyed by header names.
 * Validates required headers and enforces MAX_ROWS limit.
 */
export function parseCSV(csvText) {
    const parsed = parseCSVLines(csvText, [SORT_PATH_CSV_HEADERS.NODE_ID, SORT_PATH_CSV_HEADERS.CYCLE, SORT_PATH_CSV_HEADERS.FILL_ALGORITHM, SORT_PATH_CSV_HEADERS.PICK_ALGORITHM], "CSV");
    if (parsed.error) return parsed;

    if (parsed.lines.length - 1 > MAX_ROWS) {
        return { error: "CSV exceeds maximum " + MAX_ROWS + " cycles allowed per upload. Please split into smaller files." };
    }

    const rows = [];
    for (let rowIndex = 1; rowIndex < parsed.lines.length; rowIndex++) {
        const result = buildRowFromLine(parsed.headers, parsed.lines[rowIndex], rowIndex, "CSV");
        if (result.error) return result;
        result.row._rowNumber = rowIndex;
        rows.push(result.row);
    }
    return { rows: rows };
}

/**
 * Parses the cluster parameters CSV into a map keyed by "nodeId#cycle".
 * Each entry contains an array of cluster-level settings (ADTA, block length, capacity).
 */
export function parseClusterParamsCSV(csvText) {
    const parsed = parseCSVLines(csvText, [SORT_PATH_CLUSTER_PARAMS_HEADERS.NODE_ID, SORT_PATH_CLUSTER_PARAMS_HEADERS.CYCLE, SORT_PATH_CLUSTER_PARAMS_HEADERS.CLUSTER_NAME], "Cluster params CSV");
    if (parsed.error) return parsed;

    const clusterParamsMap = {};
    for (let rowIndex = 1; rowIndex < parsed.lines.length; rowIndex++) {
        const result = buildRowFromLine(parsed.headers, parsed.lines[rowIndex], rowIndex, "Cluster params");
        if (result.error) return result;
        const row = result.row;

        const key = row[SORT_PATH_CLUSTER_PARAMS_HEADERS.NODE_ID] + KEY_SEPARATOR + row[SORT_PATH_CLUSTER_PARAMS_HEADERS.CYCLE];
        if (!clusterParamsMap[key]) {
            clusterParamsMap[key] = [];
        }
        const adtaVal = row[SORT_PATH_CLUSTER_PARAMS_HEADERS.ADTA_ENABLED] || "";
        const blockLen = parseInt(row[SORT_PATH_CLUSTER_PARAMS_HEADERS.BLOCK_LENGTH], 10);
        const maxCap = parseInt(row[SORT_PATH_CLUSTER_PARAMS_HEADERS.MAX_CAPACITY], 10);
        clusterParamsMap[key].push({
            clusterName: row[SORT_PATH_CLUSTER_PARAMS_HEADERS.CLUSTER_NAME],
            adtaEnabled: adtaVal.toLowerCase() === TRUE_STRING,
            blockLength: !isNaN(blockLen) ? blockLen : null,
            maxCapacity: !isNaN(maxCap) ? maxCap : null,
        });
    }
    return { clusterParamsMap: clusterParamsMap };
}

/**
 * Validates a row field value against a list of allowed values.
 */
function validateFieldValue(row, field, validValues) {
    if (row[field] && !validValues.includes(row[field])) {
        return "Row " + row._rowNumber + ": Invalid " + field + " \"" + row[field] + "\". Valid options: " + validValues.join(", ");
    }
    return null;
}

/**
 * Validates that a required field is present for a given algorithm.
 */
function validateRequiredField(row, field, algorithm) {
    if (!row[field]) {
        return "Row " + row._rowNumber + ": " + field + " is required for " + algorithm;
    }
    return null;
}

/**
 * Validates required fields and nodeId match against current station.
 */
function validateRequiredFieldsAndNodeId(row, currentNodeId, cycleList) {
    const missing = [SORT_PATH_CSV_HEADERS.NODE_ID, SORT_PATH_CSV_HEADERS.CYCLE, SORT_PATH_CSV_HEADERS.FILL_ALGORITHM, SORT_PATH_CSV_HEADERS.PICK_ALGORITHM]
        .filter(function(field) { return !row[field]; });
    if (missing.length > 0) {
        return "Row " + row._rowNumber + ": Missing required fields: " + missing.join(LIST_SEPARATOR);
    }
    if (currentNodeId && row[SORT_PATH_CSV_HEADERS.NODE_ID] !== currentNodeId) {
        return "Row " + row._rowNumber + ": nodeId \"" + row[SORT_PATH_CSV_HEADERS.NODE_ID] + "\" does not match current station \"" + currentNodeId + "\"";
    }
    // Validate cycle exists in the station's known cycle list
    const cycle = row[SORT_PATH_CSV_HEADERS.CYCLE];
    if (cycleList && Array.isArray(cycleList) && cycleList.length > 0) {
        const knownCycleNames = cycleList
            .filter(function(stationCycle) { return stationCycle && stationCycle.cycle; })
            .map(function(stationCycle) { return stationCycle.cycle.cycleName; });
        if (knownCycleNames.length > 0 && !knownCycleNames.includes(cycle)) {
            return "Row " + row._rowNumber + ": Cycle \"" + cycle + "\" is not a recognized cycle for this station. Available cycles: " + knownCycleNames.join(", ");
        }
    }
    return null;
}

/**
 * Validates that excluded fields do not contain range separators (:).
 * Excluded aisles/bins/clusters must be individual labels, not ranges.
 */
function validateExcludedFieldsNoRanges(row) {
    if (containsRangeSeparator(row[SORT_PATH_CSV_HEADERS.EXCLUDED_AISLES])) {
        return "Row " + row._rowNumber + ": excluded_aisles must be individual labels separated by \";\". Ranges like \"B-7:B-10\" are not supported. List each aisle separately (e.g., \"B-7;B-8;B-9;B-10\")";
    }
    if (containsRangeSeparator(row[SORT_PATH_CSV_HEADERS.EXCLUDED_BINS])) {
        return "Row " + row._rowNumber + ": excluded_bins must be individual labels separated by \";\". Ranges are not supported.";
    }
    if (containsRangeSeparator(row[SORT_PATH_CSV_HEADERS.EXCLUDED_CLUSTERS])) {
        return "Row " + row._rowNumber + ": excluded_clusters must be individual labels separated by \";\". Ranges are not supported.";
    }
    return null;
}

/**
 * Validates auto-generated name length and checks for naming conflicts with existing configs.
 * Only checks conflicts for new cycles (not updates to existing ones).
 */
function validateNameConflicts(row, existingConfigs, allowMultiplePerCycle = false) {
    // Use user-provided name or auto-generate
    const name = resolveRowName(row);
    if (name.length > MAX_NAME_LENGTH) {
        return "Row " + row._rowNumber + ": Name \"" + name + "\" exceeds " + MAX_NAME_LENGTH + " character limit";
    }

    if (allowMultiplePerCycle) {
        // Same name on this station + cycle is an UPDATE; otherwise the name must be free at the station
        // (backend enforces per-station uniqueness), so a clash is surfaced before upload.
        if (!Array.isArray(existingConfigs) || existingConfigs.length === 0) return null;
        const nodeId = row[SORT_PATH_CSV_HEADERS.NODE_ID];
        const cycle = row[SORT_PATH_CSV_HEADERS.CYCLE];
        if (lookupSettingId(nodeId, cycle, existingConfigs, name, true)) return null;
        if (!isNameTakenAtStation(existingConfigs, nodeId, name)) return null;
        // A same-name config reaching this cycle only via cycleList is multi-cycle: a single-cycle row must not reshape it.
        const sameName = existingConfigs.filter(config => normalizeName(config.name) === normalizeName(name));
        return "Row " + row._rowNumber + ": Name \"" + name + "\" " + (countConfigsForCycle(sameName, nodeId, cycle) > 0
            ? "belongs to a multi-cycle configuration; edit it from the form, not bulk upload."
            : "already exists at station " + nodeId + " for another cycle.");
    }

    // Name conflict for CREATE
    if (existingConfigs && existingConfigs.length > 0) {
        const hasExistingForCycle = existingConfigs.some(function(config) {
            return config.nodeId === row[SORT_PATH_CSV_HEADERS.NODE_ID] && config.cycle === row[SORT_PATH_CSV_HEADERS.CYCLE];
        });
        if (!hasExistingForCycle) {
            const nameConflict = existingConfigs.some(function(config) { return config.name === name; });
            if (nameConflict) {
                return "Row " + row._rowNumber + ": Name \"" + name + "\" already exists.";
            }
        }
    }
    return null;
}

/**
 * Validates fill and pick algorithm values.
 */
function validateAlgorithms(row) {
    if (!VALID_FILL_ALGORITHMS.includes(row[SORT_PATH_CSV_HEADERS.FILL_ALGORITHM])) {
        return "Row " + row._rowNumber + ": Invalid fillPathAlgorithm \"" + row[SORT_PATH_CSV_HEADERS.FILL_ALGORITHM] + "\"";
    }
    if (!VALID_PICK_ALGORITHMS.includes(row[SORT_PATH_CSV_HEADERS.PICK_ALGORITHM])) {
        return "Row " + row._rowNumber + ": Invalid pickPathAlgorithm \"" + row[SORT_PATH_CSV_HEADERS.PICK_ALGORITHM] + "\"";
    }
    return null;
}

/**
 * Validates order type fields for fill and pick paths.
 * Includes COMBINATRIX-specific finger order and split percentage checks.
 */
function validateOrderTypes(row) {
    const fillAlgo = row[SORT_PATH_CSV_HEADERS.FILL_ALGORITHM];
    if (fillAlgo === SORT_PATH_FILL_ALGORITHMS.COMBINATRIX) {
        const fingerOrderError = validateFieldValue(row, SORT_PATH_CSV_HEADERS.FILL_FINGER_ORDER, VALID_AISLE_ORDER_TYPES);
        if (fingerOrderError) return fingerOrderError;
        const splitPercentageError = validateFieldValue(row, SORT_PATH_CSV_HEADERS.FILL_SPLIT_PERCENTAGE, VALID_SPLIT_PERCENTAGES);
        if (splitPercentageError) return splitPercentageError;
    }
    return validateFieldValue(row, SORT_PATH_CSV_HEADERS.FILL_CLUSTER_ORDER_TYPE, VALID_ORDER_TYPES)
        || validateFieldValue(row, SORT_PATH_CSV_HEADERS.FILL_AISLE_ORDER_TYPE, VALID_AISLE_ORDER_TYPES)
        || validateFieldValue(row, SORT_PATH_CSV_HEADERS.PICK_CLUSTER_ORDER_TYPE, VALID_AISLE_ORDER_TYPES)
        || validateFieldValue(row, SORT_PATH_CSV_HEADERS.PICK_AISLE_ORDER_TYPE, VALID_PICK_AISLE_ORDER_TYPES)
        || validateFieldValue(row, SORT_PATH_CSV_HEADERS.FILL_ADTA_PRIORITIZATION_ORDER, VALID_ADTA_ORDERS);
}

/**
 * Validates algorithm-specific required fields and value constraints.
 * Covers PARTIAL_COLUMN_ZEBRA, SIDELINE, and SAME_DAY algorithms.
 */
function validateAlgorithmSpecificFields(row) {
    const fillAlgo = row[SORT_PATH_CSV_HEADERS.FILL_ALGORITHM];

    // Partial column zebra: column length required and must be valid
    if (row[SORT_PATH_CSV_HEADERS.PICK_ALGORITHM] === SORT_PATH_PICK_ALGORITHMS.PARTIAL_COLUMN_ZEBRA_PATTERN) {
        const columnLengthRequiredError = validateRequiredField(row, SORT_PATH_CSV_HEADERS.PICK_COLUMN_LENGTH_ZEBRA, "PARTIAL_COLUMN_ZEBRA_PATTERN");
        if (columnLengthRequiredError) return columnLengthRequiredError;
        if (!VALID_COLUMN_LENGTHS.includes(row[SORT_PATH_CSV_HEADERS.PICK_COLUMN_LENGTH_ZEBRA])) {
            return "Row " + row._rowNumber + ": Invalid pick_column_length_zebra_path \"" + row[SORT_PATH_CSV_HEADERS.PICK_COLUMN_LENGTH_ZEBRA] + "\". Must be 3, 4, or 5";
        }
    }

    // Sideline: included_sort_zones required and max 4
    if (fillAlgo === SORT_PATH_FILL_ALGORITHMS.SIDELINE_BLOCK_FILL) {
        const sortZonesRequiredError = validateRequiredField(row, SORT_PATH_CSV_HEADERS.INCLUDED_SORT_ZONES, "SIDELINE_BLOCK_FILL");
        if (sortZonesRequiredError) return sortZonesRequiredError;
        if (toArray(row[SORT_PATH_CSV_HEADERS.INCLUDED_SORT_ZONES]).length > MAX_SIDELINE_SORT_ZONES) {
            return "Row " + row._rowNumber + ": included_sort_zones must have at most " + MAX_SIDELINE_SORT_ZONES + " entries for SIDELINE";
        }
    }

    // SAME_DAY: sort location order required for fill
    if (fillAlgo === SORT_PATH_FILL_ALGORITHMS.SAME_DAY_STANDARD_FILL) {
        const fillSortLocationError = validateRequiredField(row, SORT_PATH_CSV_HEADERS.FILL_SORT_LOCATION_ORDER, "SAME_DAY_STANDARD_FILL");
        if (fillSortLocationError) return fillSortLocationError;
    }
    // SAME_DAY: sort location order required for pick
    if (row[SORT_PATH_CSV_HEADERS.PICK_ALGORITHM] === SORT_PATH_PICK_ALGORITHMS.SAME_DAY_STANDARD_PATH) {
        return validateRequiredField(row, SORT_PATH_CSV_HEADERS.PICK_SORT_LOCATION_ORDER, "SAME_DAY_STANDARD_PATH");
    }

    return null;
}

/**
 * Returns the trimmed effective_date value for a row, or "" if absent.
 * The column is optional; a blank value means no schedule is created for the row.
 */
export function getEffectiveDate(row) {
    return (row[SORT_PATH_CSV_HEADERS.EFFECTIVE_DATE] || "").trim();
}

/**
 * Parses a strict YYYY-MM-DD string into a local-midnight Date, verifying the
 * calendar date is real (e.g. rejects 2026-02-30). Returns null if invalid.
 */
function parseEffectiveDate(value) {
    if (!EFFECTIVE_DATE_PATTERN.test(value)) return null;
    const parts = value.split("-");
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10);
    const day = parseInt(parts[2], 10);
    const date = new Date(year, month - 1, day);
    // Reject overflow (e.g. Feb 30 rolling into March) by round-tripping the components.
    if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
        return null;
    }
    return date;
}

/**
 * Validates the optional effective_date column at the UI layer.
 * - Empty is valid (no schedule is created).
 * - Must be a real YYYY-MM-DD calendar date.
 * - Must be strictly in the future; past and current (today) dates are rejected,
 *   since a schedule can only take effect on a day that has not started yet.
 *
 * When path scheduling is disabled (isPathSchedulingEnabled === false), the
 * effective_date column is ignored at upload time (the config still lands, the
 * date is never used). To keep validation consistent with execution, the date
 * is not validated in that case — a stray date column must not block an upload
 * that would otherwise succeed.
 */
function validateEffectiveDate(row, isPathSchedulingEnabled) {
    if (!isPathSchedulingEnabled) return null;

    const value = getEffectiveDate(row);
    if (!value) return null;

    const date = parseEffectiveDate(value);
    if (!date) {
        return "Row " + row._rowNumber + ": Invalid effective_date \"" + value + "\". Use format YYYY-MM-DD.";
    }

    // Station-local "today" (YYYY-MM-DD). The schedule must start strictly after it, since
    // SetPathSchedule only checks the date format and would activate a today/past date at once.
    // TODO: source the station's IANA timezone from an SCC/station-config source instead of
    // browser-local time (design Open Question) — same interim as PathScheduleTab's stationNow,
    // so both are fixed together. Zero-padded YYYY-MM-DD strings compare correctly as text.
    const stationToday = toDateInputValue(new Date());
    if (value <= stationToday) {
        return "Row " + row._rowNumber + ": effective_date \"" + value + "\" must be a future date. Past and current dates are not allowed.";
    }
    return null;
}

/**
 * Orchestrates all row-level validations in sequence.
 * Returns the first error encountered or null if all validations pass.
 *
 * isPathSchedulingEnabled gates the effective_date validation only: when
 * scheduling is off the date column is ignored at upload, so validating it
 * would reject an upload that would otherwise succeed. Defaults to false so a
 * caller that omits it does not enforce a rule execution won't honor.
 */
export function validateRow(row, clusterParamsMap, existingConfigs, currentNodeId, cycleList, isPathSchedulingEnabled) {
    return validateRequiredFieldsAndNodeId(row, currentNodeId, cycleList)
        || validateExcludedFieldsNoRanges(row)
        || validateNameConflicts(row, existingConfigs, isPathSchedulingEnabled)
        || validateAlgorithms(row)
        || validateOrderTypes(row)
        || validateAlgorithmSpecificFields(row)
        || validateEffectiveDate(row, isPathSchedulingEnabled);
}

/**
 * Validates all rows for duplicate nodeId+cycle entries.
 * Returns array of error strings (empty if no duplicates).
 */
export function validateDuplicates(rows, allowMultiplePerCycle = false) {
    // Multi-config: only a repeated station + cycle + name (case-insensitive) is a duplicate; else legacy 1:1.
    const seen = {};
    const cycleByName = {};
    const errors = [];
    rows.forEach(function(row) {
        const cycleKey = row[SORT_PATH_CSV_HEADERS.NODE_ID] + KEY_SEPARATOR + row[SORT_PATH_CSV_HEADERS.CYCLE];
        const key = allowMultiplePerCycle ? cycleKey + KEY_SEPARATOR + normalizeName(resolveRowName(row)) : cycleKey;
        if (seen[key]) {
            const detail = allowMultiplePerCycle
                ? "Duplicate entry for cycle \"" + row[SORT_PATH_CSV_HEADERS.CYCLE] + "\" and name \"" + resolveRowName(row) + "\""
                : "Duplicate entry for cycle \"" + row[SORT_PATH_CSV_HEADERS.CYCLE] + "\"";
            errors.push("Row " + row._rowNumber + ": " + detail + " (same as row " + seen[key] + ")");
            return;
        }
        seen[key] = row._rowNumber;
        // Names are unique per station (backend rule): a repeat here is on another cycle (same cycle returned above).
        const nameKey = row[SORT_PATH_CSV_HEADERS.NODE_ID] + KEY_SEPARATOR + normalizeName(resolveRowName(row));
        const first = cycleByName[nameKey];
        if (allowMultiplePerCycle && first) {
            errors.push("Row " + row._rowNumber + ": Name \"" + resolveRowName(row) + "\" is already used for cycle \""
                + first.cycle + "\" in row " + first.row + "; names must be unique per station.");
        }
        cycleByName[nameKey] = first || {row: row._rowNumber, cycle: row[SORT_PATH_CSV_HEADERS.CYCLE]};
    });
    return errors;
}

/**
 * Enforces MAX_CONFIGS_PER_CYCLE per station + cycle (existing + new rows) when multi-config is on.
 * Rows whose name is taken at the station never add a config (UPDATE, or rejected by
 * validateNameConflicts via the same isNameTakenAtStation check), so they are not counted.
 */
export function validateCycleCapacity(rows, existingConfigs, allowMultiplePerCycle = false) {
    if (!allowMultiplePerCycle) return [];
    const newByCycle = {};
    rows.forEach(function(row) {
        const nodeId = row[SORT_PATH_CSV_HEADERS.NODE_ID];
        const cycle = row[SORT_PATH_CSV_HEADERS.CYCLE];
        // Rows missing station or cycle are reported by validateRow's required-field check.
        if (!nodeId || !cycle) return;
        if (isNameTakenAtStation(existingConfigs, nodeId, resolveRowName(row))) return;
        const key = nodeId + KEY_SEPARATOR + cycle;
        newByCycle[key] = newByCycle[key] || {nodeId, cycle, rows: []};
        newByCycle[key].rows.push(row._rowNumber);
    });
    return Object.keys(newByCycle).reduce(function(errors, key) {
        const group = newByCycle[key];
        const existing = countConfigsForCycle(existingConfigs, group.nodeId, group.cycle);
        const total = existing + group.rows.length;
        if (total > MAX_CONFIGS_PER_CYCLE) {
            errors.push("Cycle \"" + group.cycle + "\" at station " + group.nodeId + " would have " + total
                + " sort path configurations (" + existing + " existing + " + group.rows.length
                + " new in rows " + group.rows.join(LIST_SEPARATOR) + "). Maximum allowed is "
                + MAX_CONFIGS_PER_CYCLE + " per cycle.");
        }
        return errors;
    }, []);
}

/**
 * Builds blockLength, maxClusterCapacityForNode, adtaClusterIdentifierForNode from cluster params.
 * Maps each cluster param into { clusterName, clusterParameterValue } format.
 */
function buildClusterParams(clusterParams) {
    if (!clusterParams || clusterParams.length === 0) {
        return { blockLength: [], maxClusterCapacityForNode: [], adtaClusterIdentifierForNode: [] };
    }

    const mapClusterParam = function(filteredParams, valueExtractor) {
        return filteredParams.map(function(clusterParam) {
            return { clusterName: clusterParam.clusterName, clusterParameterValue: valueExtractor(clusterParam) };
        });
    };

    const blockLength = mapClusterParam(
        clusterParams.filter(function(clusterParam) { return clusterParam.blockLength != null; }),
        function(clusterParam) { return String(clusterParam.blockLength); }
    );
    const maxClusterCapacityForNode = mapClusterParam(
        clusterParams.filter(function(clusterParam) { return clusterParam.maxCapacity != null; }),
        function(clusterParam) { return String(clusterParam.maxCapacity); }
    );
    const adtaClusterIdentifierForNode = mapClusterParam(
        clusterParams,
        function(clusterParam) { return clusterParam.adtaEnabled ? TRUE_STRING : FALSE_STRING; }
    );

    return { blockLength, maxClusterCapacityForNode, adtaClusterIdentifierForNode };
}

/**
 * Builds clusterSequenceForNode array from cluster params order (for STAG).
 */
function buildClusterSequenceForNode(clusterParams) {
    if (!clusterParams || clusterParams.length === 0) return null;
    return clusterParams.map(function(clusterParam, index) {
        return { clusterName: clusterParam.clusterName, clusterParameterValue: String(index + 1) };
    });
}

/**
 * Builds the standard fill fields shared by STANDARD, STANDARD_BLOCK, CLUSTER_WISE_STANDARD,
 * and FLOOR_LOCATION algorithms. Composes cluster/aisle orders with common fields and cluster param data.
 */
function buildStandardFillFields(row, commonFields, clusterParamFields, clusterOrderDefault, clusterSequenceForNode) {
    return {
        clusterOrder: {
            type: row[SORT_PATH_CSV_HEADERS.FILL_CLUSTER_ORDER_TYPE] || clusterOrderDefault,
            customSequence: toArray(row[SORT_PATH_CSV_HEADERS.FILL_CLUSTER_CUSTOM_SEQUENCE]),
            clusterSequenceForNode: clusterSequenceForNode,
        },
        aisleOrder: {
            type: row[SORT_PATH_CSV_HEADERS.FILL_AISLE_ORDER_TYPE] || SortingOrder.INCREASING,
            customSequence: toArray(row[SORT_PATH_CSV_HEADERS.FILL_AISLE_CUSTOM_SEQUENCE]),
        },
        ...commonFields,
        maxClusterCapacityForNode: clusterParamFields.maxClusterCapacityForNode,
        adtaClusterIdentifierForNode: clusterParamFields.adtaClusterIdentifierForNode,
        blockLength: clusterParamFields.blockLength,
    };
}

/**
 * Builds fill path additionalFields based on algorithm type.
 */
function buildFillAdditionalFields(row, clusterParams) {
    const fillAlgo = row[SORT_PATH_CSV_HEADERS.FILL_ALGORITHM];
    const commonFields = buildCommonFillFields(row);

    switch (fillAlgo) {
        case SORT_PATH_FILL_ALGORITHMS.STANDARD:
        case SORT_PATH_FILL_ALGORITHMS.STANDARD_BLOCK:
        case SORT_PATH_FILL_ALGORITHMS.CLUSTER_WISE_STANDARD:
            return buildStandardFillFields(row, commonFields, buildClusterParams(clusterParams), SortingOrder.INCREASING, null);
        case SORT_PATH_FILL_ALGORITHMS.FLOOR_LOCATION_STANDARD_FILL_PATH:
            return buildStandardFillFields(row, commonFields, buildClusterParams(clusterParams), SortingOrder.CUSTOM, buildClusterSequenceForNode(clusterParams));
        case SORT_PATH_FILL_ALGORITHMS.COMBINATRIX: {
            return {
                fingerOrder: {
                    type: row[SORT_PATH_CSV_HEADERS.FILL_FINGER_ORDER] || SortingOrder.INCREASING,
                    customSequence: [],
                },
                aisleOrder: {
                    type: row[SORT_PATH_CSV_HEADERS.FILL_AISLE_ORDER_TYPE] || SortingOrder.INCREASING,
                    customSequence: toArray(row[SORT_PATH_CSV_HEADERS.FILL_AISLE_CUSTOM_SEQUENCE]),
                },
                ...commonFields,
                splitPercentage: row[SORT_PATH_CSV_HEADERS.FILL_SPLIT_PERCENTAGE] || null,
                maxClusterCapacityForNode: [],
                adtaClusterIdentifierForNode: [],
                blockLength: [],
            };
        }
        case SORT_PATH_FILL_ALGORITHMS.SIDELINE_BLOCK_FILL: {
            return {
                clusterOrder: {
                    type: row[SORT_PATH_CSV_HEADERS.FILL_CLUSTER_ORDER_TYPE] || SortingOrder.INCREASING,
                    customSequence: toArray(row[SORT_PATH_CSV_HEADERS.FILL_CLUSTER_CUSTOM_SEQUENCE]),
                },
            };
        }
        case SORT_PATH_FILL_ALGORITHMS.SAME_DAY_STANDARD_FILL: {
            return {
                sortLocationOrder: {
                    type: row[SORT_PATH_CSV_HEADERS.FILL_SORT_LOCATION_ORDER] || SortingOrder.INCREASING,
                    customSequence: [],
                },
            };
        }
        default:
            return {};
    }
}

/**
 * Builds the pick cluster order object.
 */
function buildPickClusterOrder(row, defaultType) {
    return {
        type: row[SORT_PATH_CSV_HEADERS.PICK_CLUSTER_ORDER_TYPE] || defaultType,
        customSequence: toArray(row[SORT_PATH_CSV_HEADERS.PICK_CLUSTER_CUSTOM_SEQUENCE]),
    };
}

/**
 * Builds pick path additionalFields based on algorithm type.
 */
function buildPickAdditionalFields(row) {
    const pickAlgo = row[SORT_PATH_CSV_HEADERS.PICK_ALGORITHM];

    switch (pickAlgo) {
        case SORT_PATH_PICK_ALGORITHMS.SAME_DAY_STANDARD_PATH:
            return {
                clusterOrder: buildPickClusterOrder(row, null),
                sortLocationOrder: {
                    type: row[SORT_PATH_CSV_HEADERS.PICK_SORT_LOCATION_ORDER] || SortingOrder.INCREASING,
                    customSequence: [],
                },
            };
        case SORT_PATH_PICK_ALGORITHMS.FLOOR_LOCATION_STANDARD_PICK_PATH:
        case SORT_PATH_PICK_ALGORITHMS.SIDELINE_ZEBRA_PATH:
            return {
                clusterOrder: buildPickClusterOrder(row, SortingOrder.INCREASING),
                columnSplitRatio: row[SORT_PATH_CSV_HEADERS.PICK_COLUMN_SPLIT_RATIO] || DEFAULT_COLUMN_SPLIT_RATIO,
            };
        default:
            return {
                clusterOrder: buildPickClusterOrder(row, SortingOrder.INCREASING),
                aisleOrder: {
                    type: row[SORT_PATH_CSV_HEADERS.PICK_AISLE_ORDER_TYPE] || SortingOrder.INCREASING,
                    customSequence: toArray(row[SORT_PATH_CSV_HEADERS.PICK_AISLE_CUSTOM_SEQUENCE]),
                },
                columnLengthForZebraPath: row[SORT_PATH_CSV_HEADERS.PICK_COLUMN_LENGTH_ZEBRA] || null,
                columnSplitRatio: row[SORT_PATH_CSV_HEADERS.PICK_COLUMN_SPLIT_RATIO] || DEFAULT_COLUMN_SPLIT_RATIO,
            };
    }
}

/**
 * Looks up settingId for a given nodeId+cycle from existing configurations.
 */
export function lookupSettingId(nodeId, cycle, existingConfigs, name, matchByName = false) {
    // matchByName (multi-config): same name UPDATEs it; a new name CREATEs instead of overwriting the first match.
    if (!existingConfigs || existingConfigs.length === 0) return null;
    const match = existingConfigs.find(function(config) {
        if (!(config.nodeId === nodeId && config.cycle === cycle)) return false;
        return !matchByName || normalizeName(config.name) === normalizeName(name);
    });
    return match ? match.settingId : null;
}

/**
 * Converts a validated CSV row into a complete sortPathSetting API payload.
 * Resolves settingId from existing configs to determine create vs update.
 */
export function buildSortPathSettingFromRow(row, clusterParamsMap, existingConfigs, allowMultiplePerCycle = false) {
    const nodeId = row[SORT_PATH_CSV_HEADERS.NODE_ID];
    const cycle = row[SORT_PATH_CSV_HEADERS.CYCLE];
    const key = nodeId + KEY_SEPARATOR + cycle;
    const clusterParams = clusterParamsMap ? clusterParamsMap[key] : null;

    // Auto-generate name: cycle_algorithm, truncate if needed
    const name = resolveRowName(row);
    const settingId = lookupSettingId(nodeId, cycle, existingConfigs, name, allowMultiplePerCycle);

    return {
        settingId: settingId,
        name: name,
        cycle: cycle,
        nodeId: nodeId,
        binType: row[SORT_PATH_CSV_HEADERS.BIN_TYPE] || null,
        volumeType: null,
        cycleList: null,
        injectionType: null,
        inductLocationType: null,
        status: SORT_PATH_SETTING_STATUS.UPDATED,
        additionalParams: null,
        excludedLocations: {
            excludedClusters: toArray(row[SORT_PATH_CSV_HEADERS.EXCLUDED_CLUSTERS]),
            excludedAisles: toArray(row[SORT_PATH_CSV_HEADERS.EXCLUDED_AISLES]),
            excludedBins: toArray(row[SORT_PATH_CSV_HEADERS.EXCLUDED_BINS]),
            excludedFloorLocations: [],
        },
        includedLocations: {
            includedAisles: parseRanges(row[SORT_PATH_CSV_HEADERS.INCLUDED_AISLES], INCLUDED_AISLE_START, INCLUDED_AISLE_END),
            includedColumns: toArray(row[SORT_PATH_CSV_HEADERS.INCLUDED_COLUMNS]),
            includedSortZones: toArray(row[SORT_PATH_CSV_HEADERS.INCLUDED_SORT_ZONES]),
            includedSortLocations: parseRanges(row[SORT_PATH_CSV_HEADERS.INCLUDED_SORT_LOCATIONS], INCLUDED_LOCATION_START, INCLUDED_LOCATION_END),
        },
        fillPathSetting: {
            algorithm: row[SORT_PATH_CSV_HEADERS.FILL_ALGORITHM],
            additionalFields: buildFillAdditionalFields(row, clusterParams),
        },
        pickPathSetting: {
            algorithm: row[SORT_PATH_CSV_HEADERS.PICK_ALGORITHM],
            additionalFields: buildPickAdditionalFields(row),
        },
    };
}
